import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EmailVerificationForm = ({ onNext, onBack, formData }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  // Mock verification code for demo
  const mockVerificationCode = "123456";

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    
    if (!verificationCode.trim()) {
      setErrors({ code: 'Verification code is required' });
      setIsVerifying(false);
      return;
    }

    if (verificationCode.length !== 6) {
      setErrors({ code: 'Verification code must be 6 digits' });
      setIsVerifying(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      if (verificationCode === mockVerificationCode) {
        setErrors({});
        onNext();
      } else {
        setErrors({ code: 'Invalid verification code. Please try again.' });
      }
      setIsVerifying(false);
    }, 1500);
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendTimer(60);
    
    // Simulate API call
    setTimeout(() => {
      setIsResending(false);
      setErrors({});
    }, 1000);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    if (errors.code) {
      setErrors({});
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Mail" size={32} className="text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Check Your Email</h3>
        <p className="text-sm text-muted-foreground">
          We've sent a 6-digit verification code to
        </p>
        <p className="text-sm font-medium text-foreground">{formData.email}</p>
      </div>

      <form onSubmit={handleVerificationSubmit} className="space-y-6">
        <div className="space-y-4">
          <Input
            label="Verification Code"
            type="text"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={handleCodeChange}
            error={errors.code}
            description={`Demo code: ${mockVerificationCode}`}
            className="text-center text-lg tracking-widest"
            maxLength={6}
            required
          />
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            disabled={verificationCode.length !== 6}
            loading={isVerifying}
            iconName="Check"
            iconPosition="right"
          >
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </Button>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Didn't receive the code?
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResendCode}
              disabled={resendTimer > 0 || isResending}
              loading={isResending}
              iconName="RefreshCw"
              iconPosition="left"
            >
              {resendTimer > 0 
                ? `Resend in ${resendTimer}s` 
                : isResending 
                  ? 'Sending...' :'Resend Code'
              }
            </Button>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="lg"
          fullWidth
          onClick={onBack}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Back to Account Details
        </Button>
      </form>

      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Secure Verification</p>
            <p className="text-xs text-muted-foreground">
              Your email verification is protected with 256-bit encryption and expires in 10 minutes for security.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationForm;